import useAuth from "@app/context/AuthContext";
import { setsAnalysisPhasesFe55 } from "@app/pages/samples/config/phases/AnalysisPhases";
import {
  DEFAULT_AMOUNT_SAMPLE,
  DEFAULT_CRITERION,
  DEFAULT_EQUIPMENT,
  DEFAULT_LABORATORY,
  DEFAULT_VALID_PREPARATION,
} from "@app/pages/samples/utils/constants";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useSample } from "@app/pages/samples/utils/useSample";
import useTabs from "@app/pages/samples/utils/useTab";
import { UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
} from "@app/types/samples.types";
import { formatDateOrNow } from "@app/utils/formatDate";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentMeasurement?: AnalysisCurrent,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  typeAnalysis?: any,
  idMuestra?: string
) => {
  const currentMeasurementIndex: any =
    currentPreparationIndex?.aa_measures?.[currentMeasurement?.index as number];

  const currentCountingIndex: any =
    currentMeasurementIndex?.countings?.[currentCounting?.index as number];
  const currentCalculationIndex =
    currentCountingIndex?.calculations[currentCalculation?.index as number];
  const currentSampling = sampling?.current;
  const defaultTypeUnit = sampling?.current?.unit_types;
  const currentSample = useSample.getState().get(Number(idMuestra));

  return {
    analysis_analyst: currentPreparationIndex?.analyst,
    //preparation
    preparation_procedure:
      currentPreparationIndex?.preparation_procedure || undefined,
    start_date: currentPreparationIndex?.start_date || dayjs().toISOString(),
    analysis_amount_sample: currentPreparationIndex?.amount_sample || 0,
    analysis_equipment: currentPreparationIndex?.equipment || [],
    scintillator_lot_number:
      currentPreparationIndex?.scintillator_lot_number || undefined,
    analysis_unit_types: currentPreparationIndex?.unit_types || defaultTypeUnit,
    valid_preparation:
      currentPreparationIndex?.valid_preparation || DEFAULT_VALID_PREPARATION,
    //mesurement
    measurement_analyst: currentMeasurementIndex?.analyst || user,
    measurement_processing_procedure:
      currentMeasurementIndex?.procedure || undefined,
    measurement_equipment: currentMeasurementIndex?.equipment || [],
    measurement_date_time: formatDateOrNow(
      currentMeasurementIndex?.start_date_time
    ),
    mass_glass_without_sample:
      currentMeasurementIndex?.mass_glass_without_sample || null,
    mass_glass_with_sample:
      currentMeasurementIndex?.mass_glass_with_sample || null,
    mass_sample: currentMeasurementIndex?.mass_sample || null,
    aliquot_s: currentMeasurementIndex?.aliquot_s || null,
    carrier_volume_added_sample:
      currentMeasurementIndex?.carrier_volume_added_sample || 1,
    carrier_concentration:
      currentPreparationIndex?.scintillator_lot_number?.concentration || 0,
    carrier_mass: currentMeasurementIndex?.carrier_mass,
    dissolution_flask_volume:
      currentMeasurementIndex?.dissolution_flask_volume || 50,
    atomic_absorption_measurement:
      currentMeasurementIndex?.atomic_absorption_measurement || null,
    counting_by_one: currentMeasurementIndex?.counting_by_one,
    vial_sample_quantity: currentMeasurementIndex?.vial_sample_quantity,
    valid_measure: "N/A",
    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure: currentCountingIndex?.counting_procedure || undefined,
    counting_equipment: currentCountingIndex?.equipo
      ? currentCountingIndex?.equipo
      : typeAnalysis
      ? DEFAULT_EQUIPMENT[`${typeAnalysis?.id}`]
      : [],
    counting_date: currentCountingIndex?.counting_date || dayjs().toISOString(),
    sqp_tsie_white: currentCountingIndex?.sqp_tsie_white || "",
    sqp_tsie_sample: currentCountingIndex?.sqp_tsie_sample || "",
    applied_criterion:
      currentCountingIndex?.applied_criterion || DEFAULT_CRITERION,
    variation_sqp_tsie: currentCountingIndex?.variation_sqp_tsie || "",
    counting_valid: currentCountingIndex?.counting_valid || "",
    sample_cpm: currentCountingIndex?.sample_cpm || "",
    gross_white_cpm: currentCountingIndex?.gross_white_cpm || "",
    total_sample_counting_time:
      currentCountingIndex?.total_sample_counting_time || "",
    total_white_counting_time:
      currentCountingIndex?.total_white_counting_time || "",
    cmp_white_quimioluminiscencia:
      currentCountingIndex?.cmp_white_quimioluminiscencia || "",
    cpm_sample_quimioluminiscencia:
      currentCountingIndex?.cpm_sample_quimioluminiscencia || "",

    //calculation
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_equipment: currentCalculationIndex?.equipment
      ? currentCalculationIndex?.equipment
      : [currentCountingIndex?.equipo],
    calculation_analyst: currentCalculationIndex?.analyst || user,
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_amount_sample:
      currentPreparationIndex?.amount_sample || undefined,
    calculation_units: currentPreparationIndex?.unit_types || undefined,
    calculation_tsie_white: currentCountingIndex?.sqp_tsie_white || undefined,
    calculation_tsie_sample: currentCountingIndex?.sqp_tsie_sample || undefined,
    calculation_shared: currentSample?.shared ? "Si" : "No",
    calculation_counting_date: currentCountingIndex?.counting_date
      ? formatDateOrNow(currentCountingIndex?.counting_date)
      : undefined,
    sample_gross_cpm: currentCountingIndex?.sample_cpm || undefined,
    gross_target_cpm: currentCountingIndex?.gross_white_cpm || undefined,
    total_time_counting:
      currentCountingIndex?.total_sample_counting_time || undefined,
    calculation: currentCalculationIndex?.calculation || "",
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || undefined,
    sampledAirVolumeSilicagel:
      currentPreparationIndex?.sampledAirVolumeSilicagel || "",
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
  };
};

const Fe55Form = () => {
  const { tabValueAnalysis } = useTabs();

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { user } = useAuth();

  const {
    currentPreparation,
    currentMeasurement,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const currentPreparationObject = preparationsList.filter((preparation) =>
    preparation.analysis_type.some(
      (analysis) => analysis.id === form.getValues("typeAnalysis")?.id
    )
  )[currentPreparation.index as number];

  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentMeasurement,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          form.getValues("typeAnalysis"),
          idMuestra
        ),
      };

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]);

  const CurrentComponent = setsAnalysisPhasesFe55[tabValueAnalysis].component;
  return <CurrentComponent />;
};

export default Fe55Form;
